import React from "react";
import {
  Platform,
  Pressable,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from "react-native";
import { Feather, Fontisto } from "@expo/vector-icons";
import { LinearGradient } from "expo-linear-gradient";

import OnboardingBaseScreen from "./Components/OnboardingBaseScreen";

import type { AppUserSession } from "@/schemas/auth/session";
import AccountAvatarImage from "@/screens/Account/Components/AccountHeader/Components/AccountAvatarImage";
import type { FeatherIconName } from "@/schemas/icons";
import { Spacings } from "@/styling/spacings";
import { useAvatarUpload } from "@/hooks/media";

type Props = {
  session: AppUserSession;
};

const COMMUNITY_VALUES = [
  {
    icon: "check-circle" as FeatherIconName,
    title: "Acceptance",
    description: "Embrace and celebrate each person's unique identity.",
  },
  {
    icon: "life-buoy" as FeatherIconName,
    title: "Support",
    description: "We lift each other up with care and understanding.",
  },
  {
    icon: "shield" as FeatherIconName,
    title: "Safe Space",
    description: "Your safety and comfort are our priority.",
  },
  {
    icon: "users" as FeatherIconName,
    title: "Inclusive",
    description: "All identities and backgrounds are valued.",
  },
  {
    icon: "star" as FeatherIconName,
    title: "Authentic",
    description: "Build genuine relationships and connections.",
  },
  {
    icon: "unlock" as FeatherIconName,
    title: "Open",
    description: "Share freely in a judgment-free space.",
  },
];

function CommunityValues(): React.JSX.Element {
  const renderValueCard = (
    icon: FeatherIconName,
    title: string,
    description: string,
    key: string,
  ): React.JSX.Element => (
    <View style={styles.card} key={key}>
      <View style={styles.cardHeader}>
        <Feather name={icon} size={24} color="#4a4a4a" />
        <Text style={styles.cardTitle} numberOfLines={1}>
          {title}
        </Text>
      </View>
      <Text style={styles.cardDescription}>{description}</Text>
    </View>
  );

  return (
    <>
      <Text style={styles.title}>Our Community Values</Text>
      <View style={styles.cardsContainer}>
        {COMMUNITY_VALUES.map((value) =>
          renderValueCard(
            value.icon,
            value.title,
            value.description,
            value.title,
          ),
        )}
      </View>
    </>
  );
}

function OnboardingProfileAvatarScreen({ session }: Props): React.JSX.Element {
  const [acceptedValues, setAcceptedValues] = React.useState(false);
  const { isLoading, pickAndUploadImageAsAvatar } = useAvatarUpload(session);

  const stageMainComponent: React.JSX.Element = (
    <View style={styles.container}>
      <TouchableOpacity
        onPress={() => pickAndUploadImageAsAvatar()}
        disabled={isLoading}
        style={styles.avatarTouchableStyle}
      >
        <AccountAvatarImage
          avatar={session.identity.avatar}
          size={150}
          onUploadPress={() => pickAndUploadImageAsAvatar()}
          isUploading={isLoading}
          completionPercentage={100}
        />
      </TouchableOpacity>
      <LinearGradient
        colors={[
          "#FFB3B3",
          "#FFD6A5",
          "#FFFACD",
          "#C7F9CC",
          "#A0C4FF",
          "#BDB2FF",
          "#FFC6FF",
        ]}
        style={styles.rainbowLine}
        start={{ x: 0, y: 0 }}
        end={{ x: 1, y: 0 }}
      />

      <CommunityValues />

      <View style={styles.checkboxContainer}>
        <Pressable
          style={styles.checkboxRow}
          onPress={() => setAcceptedValues(!acceptedValues)}
          accessibilityRole="checkbox"
          accessibilityState={{ checked: acceptedValues }}
          hitSlop={{ top: 20, bottom: 20, left: 20, right: 100 }}
        >
          <View style={styles.checkboxIconWrapper}>
            <Fontisto
              name={acceptedValues ? "checkbox-active" : "checkbox-passive"}
              size={18}
              color="black"
            />
          </View>
          <Text style={styles.checkboxLabel}>
            I agree to uphold these community values
          </Text>
        </Pressable>
      </View>
    </View>
  );

  return (
    <OnboardingBaseScreen
      session={session}
      title="Add Your Avatar"
      subtitle="Add a photo of your face so people know who they're connecting with and make a great first impression."
      canContinue={session.identity.avatar !== null && acceptedValues}
      stageMainComponent={stageMainComponent}
      buttonLabel="Continue"
    />
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    marginHorizontal: Spacings.s3,
    alignItems: "center",
    justifyContent: "center",
  },
  avatarTouchableStyle: {
    alignItems: "center",
    marginBottom: Spacings.s2,
  },
  title: {
    fontSize: 20,
    fontWeight: "bold",
    textAlign: "center",
    marginBottom: Spacings.s5,
    color: "rgb(11,71,135)",
  },
  rainbowLine: {
    height: 2,
    borderRadius: 2,
    marginVertical: Spacings.s5,
    width: "100%",
  },
  cardsContainer: {
    flexDirection: "column",
    alignItems: "center",
    alignSelf: "stretch",
  },
  card: {
    backgroundColor: "#fff",
    borderRadius: 20,
    paddingHorizontal: Spacings.s4,
    paddingVertical: Spacings.s3,
    marginBottom: Spacings.s2,
    width: "100%",
    ...Platform.select({
      ios: {
        shadowColor: "#000",
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.1,
        shadowRadius: 4,
      },
      android: {
        elevation: 3,
      },
    }),
  },
  cardHeader: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: Spacings.s2,
  },
  cardTitle: {
    fontSize: 18,
    fontWeight: "bold",
    marginLeft: Spacings.s2,
    color: "#333",
    flex: 1,
  },
  cardDescription: {
    fontSize: 14,
    color: "#666",
    flex: 1,
  },
  checkboxContainer: {
    marginTop: Spacings.s4,
    alignSelf: "stretch",
    paddingLeft: Spacings.s1,
  },
  checkboxRow: {
    flexDirection: "row",
    alignItems: "center",
  },
  checkboxIconWrapper: {
    width: 24, // slightly larger than icon size to ensure no shift
    alignItems: "center",
    justifyContent: "center",
    marginRight: Spacings.s2,
  },
  checkboxLabel: {
    fontSize: 16,
    color: "#333",
    flexShrink: 1,
  },
});

export default OnboardingProfileAvatarScreen;
